import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ImpersonationTarget } from '@/lib/impersonation/types';
import { setSoloLecturaVerComo } from '@/lib/impersonation/solo-lectura';
import { setUidVerComo } from '@/lib/impersonation/ver-como-lectura';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Quién se está impersonando AHORA, sin que el núcleo tenga que conocer los 12
 * contextos de impersonación que existen (uno por portal).
 *
 * Cada portal sigue con su contexto propio (ahí vive su data específica) y solo
 * **publica** aquí el usuario que está viendo, con `usePublishImpersonationTarget`.
 * Los providers montados en toda la app publican desde el LAYOUT de su portal
 * (`usePublicarVerComo<Portal>`), para que salir del portal quite la solo lectura;
 * los que viven dentro de su layout publican desde el provider. Los consumidores
 * globales leen de un solo lugar.
 *
 * Puede haber varios publicadores a la vez, así que cada uno tiene su propia ranura: uno que publica `null`
 * no borra el impersonado de otro. El target efectivo es el primero no nulo.
 *
 * Con target publicado la app entra en SOLO LECTURA: este provider prende el flag
 * de `lib/impersonation/solo-lectura`, que el `fetch` del cliente de Supabase
 * consulta para rechazar escrituras REST y de Storage.
 *
 * Si ningún portal publica nada, el target es null y todo se resuelve con el
 * perfil logueado.
 *
 * "Ver como" uno mismo no es impersonar: un target con el correo de la sesión
 * se descarta. Antes ponía al admin en solo lectura sobre sus propios datos (p. ej.
 * en Tickets, donde el selector lista al propio usuario entre los asignables) y
 * no podía crear nada.
 */

interface ImpersonationTargetContextType {
  target: ImpersonationTarget | null;
  publish: (slot: string, target: ImpersonationTarget | null) => void;
}

const ImpersonationTargetContext = createContext<ImpersonationTargetContextType>({
  target: null,
  publish: () => {},
});

export function ImpersonationTargetProvider({ children }: { children: ReactNode }) {
  const [slots, setSlots] = useState<Record<string, ImpersonationTarget>>({});

  const publish = useCallback((slot: string, next: ImpersonationTarget | null) => {
    setSlots((prev) => {
      if (next == null) {
        if (!(slot in prev)) return prev;
        const copia = { ...prev };
        delete copia[slot];
        return copia;
      }
      return { ...prev, [slot]: next };
    });
  }, []);

  const { user } = useAuth();
  const emailSesion = (user?.email ?? '').trim().toLowerCase();
  // Sin sesión cargada todavía no se sabe quién es "uno mismo": un "Ver como" guardado a uno mismo
  // activaba solo lectura un instante al recargar. Sin sesión no hay a quién impersonar.
  const target = useMemo(
    () =>
      emailSesion
        ? Object.values(slots).find((t) => (t.email ?? '').trim().toLowerCase() !== emailSesion) ?? null
        : null,
    [slots, emailSesion],
  );

  useEffect(() => {
    setSoloLecturaVerComo(target != null, target?.nombre ?? target?.email ?? null);
    setUidVerComo(target?.authUserId ?? null);
  }, [target]);

  // Las RPC de lectura que toman `x-ver-como` responden distinto según la persona, y la mayoría
  // de sus claves de caché no la llevan: al cambiar de persona (o volver a la vista propia) se
  // invalida la caché (lo activo se vuelve a pedir; lo inactivo, al montarse) para no quedarse
  // con datos de otra identidad. Invalidar y no reiniciar: menús y perfil no parpadean.
  const queryClient = useQueryClient();
  const uidPrevio = useRef<string | null>(null);
  useEffect(() => {
    const uid = target?.authUserId ?? null;
    if (uid === uidPrevio.current) return;
    const habia = uidPrevio.current !== null;
    uidPrevio.current = uid;
    if (uid !== null || habia) void queryClient.invalidateQueries();
  }, [target?.authUserId, queryClient]);

  useEffect(() => () => {
    setSoloLecturaVerComo(false);
    setUidVerComo(null);
  }, []);

  const value = useMemo(() => ({ target, publish }), [target, publish]);
  return (
    <ImpersonationTargetContext.Provider value={value}>{children}</ImpersonationTargetContext.Provider>
  );
}

export function useImpersonationTarget(): ImpersonationTarget | null {
  return useContext(ImpersonationTargetContext).target;
}

/**
 * Publica el impersonado de un portal. Se limpia al desmontar, para que salir del
 * portal no deje un target colgado afectando al resto de la app.
 *
 * Migrar un portal = llamar a esto con su target. Nada más.
 */
export function usePublishImpersonationTarget(target: ImpersonationTarget | null): void {
  const { publish } = useContext(ImpersonationTargetContext);
  const slot = useId();

  // Se compara por valor: los portales arman el objeto en cada render y comparar
  // por referencia dispararía un set en bucle.
  const key = target
    ? `${target.email}|${target.personaId}|${target.nombre ?? ''}|${target.rolId}|${target.rolNombre ?? ''}|${target.authUserId ?? ''}`
    : null;

  useEffect(() => {
    publish(slot, target);
    return () => publish(slot, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, slot, publish]);
}
